/**
 * Bounded Context: Context — Pinia store
 *
 * Mantém goal ativo + curiosity gaps. Cap de 3 gaps (working memory).
 * Constitucional:
 * - Cap de 3 gaps (CL-12) — enforçado em `addCuriosityGap`.
 * - CC-08 (Motivação cultivada): zero "streak" / "X dias desde início" visível.
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import type { CuriosityGap, Goal } from "./types";

const MAX_GAPS = 3;

export const useContextStore = defineStore("context", () => {
  const activeGoal = ref<Goal | null>(null);
  const curiosityGaps = ref<CuriosityGap[]>([]);

  function setGoal(goal: Goal | null): void {
    activeGoal.value = goal;
  }

  /**
   * Adiciona curiosity gap. Se exceder MAX_GAPS (3), descarta o mais antigo
   * (FIFO). Working memory cap (CL-12): nunca crescer sem limite.
   * Constitucional: nunca shrinka sem ordem explícita.
   */
  function addCuriosityGap(gap: CuriosityGap): boolean {
    if (curiosityGaps.value.some((g) => g.id === gap.id)) return true;
    const next = [...curiosityGaps.value, gap];
    const trimmed = next.length > MAX_GAPS ? next.slice(next.length - MAX_GAPS) : next;
    curiosityGaps.value = trimmed;
    return true;
  }

  function clearCuriosityGaps(): void {
    curiosityGaps.value = [];
  }

  return {
    activeGoal,
    curiosityGaps,
    maxGaps: MAX_GAPS,
    setGoal,
    addCuriosityGap,
    clearCuriosityGaps,
  };
});
